import Eventable from 'utils/eventable';
import utils from 'utils/utils';
import session, { PlaybackSessionData } from './session';
import { isCustomIntervalFalsy } from './helpers';
import type { StreamModel } from '../../model/stream';
import type { AssetModel } from '../../model/asset';
import type { Model } from 'backbone';
import type { InternalPlayer } from '../model';

export type PlaybackEventData = {
    playMethod: string | null;
    previousAssetId?: number;
    playbackSource?: string;
    duration: number;
    position?: number;
    start?: true;
    count?: number;
};

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: Play, Pause, Stop, Watch
 */
class PlaybackEvents extends Eventable {
    player: InternalPlayer;
    previousTime: number;
    session: PlaybackSessionData;
    beforeStreamStart: boolean;
    beforeUnload: ['pagehide' | 'beforeunload', () => void, boolean];
    playMethod: null | jwplayer.PlayReason;
    vodInterval?: number;
    liveInterval?: number;
    liveTimeOffset: number | null = null;
    previousAssetId: null | string = null;
    watchData: {
        time: number;
        duration: number;
    };

    constructor(
        player: InternalPlayer,
        options: {
            vodInterval?: number;
            liveInterval?: number;
        },
    ) {
        super();

        this.onPlaylistItem = this.onPlaylistItem.bind(this);
        this.onAssetPlay = this.onAssetPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPlayNext = this.onPlayNext.bind(this);
        this.onLiveTime = this.onLiveTime.bind(this);
        this.onTime = this.onTime.bind(this);
        this.onComplete = this.onComplete.bind(this);

        this.player = player;
        this.watchData = {
            time: 0,
            duration: -1,
        };

        this.previousTime = -1;

        this.session = this.createSession();
        this.beforeStreamStart = true;
        this.beforeUnload = [
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onBeforeUnload.bind(this),
            true,
        ];
        this.playMethod = null;

        this.vodInterval = options?.vodInterval;
        this.liveInterval = options?.liveInterval;

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem);
        this.listenTo(this.player, 'assetPlay', this.onAssetPlay);
        this.listenTo(this.player, 'pause', this.onPause);
        this.listenTo(this.player, 'play', this.onPlay);
        this.listenTo(this.player, 'playNext', this.onPlayNext);

        if (this.stream.isLive()) {
            this.listenTo(this.player, 'time', this.onLiveTime);
        } else {
            this.listenTo(this.player, 'time', this.onTime);
        }

        this.listenTo(
            this.player,
            'RecommendedPlugin:nextAssetPlay',
            (
                _asset: AssetModel,
                mode: 'auto' | 'click',
                opts: { source?: string },
            ) => {
                let playbackSource =
                    mode === 'auto' ? 'autoplayNext' : 'manualNext';
                if (opts.source && opts.source !== 'byId') {
                    playbackSource += `-${opts.source}`;
                }
                return this.onRecommendedPlay(playbackSource);
            },
        );

        this.listenTo(this.player, 'nextAutoAdvance', () =>
            this.onRecommendedPlay('autoplayNext-playlist'),
        );

        this.listenTo(this.player, 'nextClick', () =>
            this.onRecommendedPlay('manualNext-playlist'),
        );

        this.listenTo(this.player, 'relatedReady', () => {
            if (!this.jwRelatedPlugin) {
                return;
            }
            this.jwRelatedPlugin.on('feedClick', () => {
                this.beforeStreamStart = false;
                this.onComplete();
                this.onRecommendedPlay('manualNext-playlist');
            });
        });

        this.listenTo(this.player, 'playlistComplete', () => {
            this.listenToOnce(this.player, 'displayClick', () =>
                this.onRecommendedPlay('manualNext-playlist'),
            );
        });

        this.listenTo(this.player, 'RecommendedPlugin:playRecommended', () => {
            this.onRecommendedPlay('manualNext-grid');
        });

        this.listenToOnce(
            this.player,
            'play',
            ({ playReason }: jwplayer.PlayParam) => {
                this.playMethod = playReason;
            },
        );

        window.addEventListener(...this.beforeUnload);
    }

    get eventData(): PlaybackEventData {
        const data: PlaybackEventData = {
            playMethod: this.playMethod,
            duration: this.watchData.duration,
        };

        const { previousAssetId } = this.session;

        if (previousAssetId) {
            data.previousAssetId = previousAssetId;
            data.playbackSource = this.session.playbackSource;
        }

        try {
            data.position = Math.floor(this.player.getCurrentTime());
        } catch {
            // could not parse time
            // do not set any value in this case
        }

        return data;
    }

    get stream() {
        try {
            return (this.player.config?.stream || {}) as StreamModel;
        } catch {
            return {} as StreamModel;
        }
    }

    get jw() {
        try {
            return this.player.getJWPlayer();
        } catch {
            return null;
        }
    }

    get jwRelatedPlugin() {
        try {
            return (
                (this.jw?.getPlugin('related') as jwplayer.JWPlugin & Model) ||
                null
            );
        } catch {
            return null;
        }
    }

    getPosition() {
        try {
            return Math.floor(this.player.getCurrentTime());
        } catch {
            return 0;
        }
    }

    createSession(): PlaybackSessionData {
        // node is unique player identifier
        const node = this.player.config!.get('node') as unknown;
        const id = (node as { id: string }).id || (node as string);

        /**
         * Initialize session object to keep reference
         */
        if (!session[id]) {
            session[id] = {};
        }

        return session[id]!;
    }

    trackTimeEvent({
        time,
        defaultInterval = 1,
        customInterval,
    }: {
        time: number;
        defaultInterval?: number;
        customInterval?: number;
    }) {
        const eventInterval = utils.isNumber(customInterval)
            ? customInterval!
            : defaultInterval;

        // track event data with one second interval
        if (time % 1 === 0 && this.previousTime !== time) {
            this.watchData.duration += 1;
            this.previousTime = time;
        }

        if (time % eventInterval === 0 && this.watchData.time !== time) {
            this.watchData.time = time;

            // skip when custom interval is turned off
            if (isCustomIntervalFalsy(customInterval)) {
                return;
            }

            this.trigger(
                'Watch',
                Object.assign(this.eventData, {
                    position: time,
                }),
            );
        }
    }

    onBeforeUnload() {
        if (!this.beforeStreamStart) {
            this.onComplete();
        }
    }

    onPlaylistItem() {
        this.listenToOnce(this.player, 'complete nextClick', this.onComplete);
        const track = this.trigger.bind(
            this,
            'Load',
            Object.assign(this.eventData, {
                position: this.stream.getPlaybackTime('begin') || 0,
                duration: 0,
            }),
        );

        /**
         * Only first playback has to wait for viewable event
         * Playnext/grid will work without it
         */
        if (this.session.wasViewed === true) {
            track();
        } else {
            // wait for player to be viewable
            this.listenTo(this.jw, 'viewable', (data) => {
                if (data.viewable) {
                    track();
                    this.stopListening(this.jw, 'viewable');
                    this.session.wasViewed = true;
                }
            });
        }

        this.previousAssetId = null;
    }

    onAssetPlay() {
        this.beforeStreamStart = false;
        this.liveTimeOffset = null;

        return this.trigger(
            'Play',
            Object.assign(this.eventData, {
                start: true,
                duration: 0,
            }),
        );
    }

    onPause() {
        this.trigger('Pause', this.eventData);
    }

    onPlay() {
        // play can't be triggered before assetPlay
        if (!this.beforeStreamStart) {
            this.trigger('Play', this.eventData);
        }
    }

    /**
     * Intervals for VOD are defined as follow
     *
     * every 5s - during the first 10 seconds of content playback (no matter of where playback has started)
     * every 10s - for the range (10-60s) of content playback, after seeking intervals should be triggered in same time
     * every 30s - for the rest
     * seek 42, then first trigger should be in 50
     */
    onTime(eventTime: number) {
        const time = Math.floor(eventTime);
        let eventInterval;

        if (eventTime <= 10) {
            eventInterval = 5;
        } else if (eventTime <= 120) {
            eventInterval = 10;
        } else {
            eventInterval = 30;
        }
        this.trackTimeEvent({
            time,
            defaultInterval: eventInterval,
            customInterval: this.vodInterval,
        });
    }

    /**
     * Track live events by checking time from flight time to current date
     * DVR seek is also included in calculations
     *
     * For no DVR streams jw.getPosition() is unreliable, so instead we
     * track elapsed watch time since the viewer joined (starting from 0),
     * anchored to broadcastElapsedTime at join via liveTimeOffset.
     */
    onLiveTime() {
        const duration = this.jw!.getDuration();
        const broadcastElapsedTime = Math.floor(
            this.stream.getTimeToStart() * -1,
        );

        let time;

        if (Number.isFinite(duration)) {
            // DVR: absolute position within the stream, DVR seeks included
            time = broadcastElapsedTime + this.jw!.getPosition();
        } else {
            // no DVR: elapsed watch time since the viewer joined
            if (this.liveTimeOffset === null) {
                this.liveTimeOffset = broadcastElapsedTime;
            }
            time = broadcastElapsedTime - this.liveTimeOffset;
        }

        // constant tracking interval
        this.trackTimeEvent({
            time: Math.floor(time),
            defaultInterval: 30,
            customInterval: this.liveInterval,
        });
    }

    onComplete(reason?: string) {
        if (reason === 'manualNext') {
            this.onRecommendedPlay('manualNext');
        }

        /**
         * Track stop event only when playback has started
         */
        if (!this.beforeStreamStart) {
            this.trigger('Stop', this.eventData);
            // store asset id in memory to attach it to play next video
            this.session.previousAssetId = this.stream.getId();
        }
        this.beforeStreamStart = true;
    }

    onPlayNext(
        _id: number,
        options: {
            pulse?: { playbackSource: string };
            source: string;
        },
    ) {
        if (options && options.pulse) {
            Object.assign(this.session, options.pulse);
        }
    }

    onRecommendedPlay(playbackSource: string) {
        this.session.playbackSource = playbackSource;
    }

    destroy() {
        window.removeEventListener(...this.beforeUnload);
        this.stopListening();
    }
}

export default PlaybackEvents;
